import React from "react";
import { AutoAccion, AutoRegla } from "../../../../services/v2/plantillas.service";

interface Props {
	reglas: AutoRegla[];
	setReglas: (r: AutoRegla[]) => void;
	/** Textos de los botones de respuesta rápida: cada uno puede tener su respuesta. */
	botones: string[];
	statuses: { id: number; nombre: string }[];
	archivos: Record<string, File>;
	setArchivos: (a: Record<string, File>) => void;
	editable: boolean;
}

const DATOS = [{ k: "nombre", t: "Nombre" }, { k: "asesora", t: "Asesora" }, { k: "saludo", t: "Saludo" }, { k: "curso", t: "Curso" }];
let sig = 1;

/**
 * Respuestas automáticas (Esteban, 2026-10-05): cuando el cliente toca un botón de la plantilla, o contesta cualquier
 * cosa, WAPP le contesta solo (texto, foto, video o PDF), le cambia el status y avisa a la asesora. Sin notas de voz:
 * una automática se ve como si la asesora la mandara, o como audio adjunto.
 */
const AutoRespuestas: React.FC<Props> = ({ reglas, setReglas, botones, statuses, archivos, setArchivos, editable }) => {
	const disparos = [...botones.filter((b) => b.trim()).map((b) => "boton:" + b.trim()), "cualquiera"];
	const libres = disparos.filter((d) => !reglas.some((r) => r.disparo === d));
	const nombreDisparo = (d: string) => d === "cualquiera" ? (botones.length ? "Si contesta otra cosa" : "Cuando conteste cualquier cosa") : "Si toca «" + d.slice(6) + "»";

	const setRegla = (i: number, r: AutoRegla) => setReglas(reglas.map((x, j) => (j === i ? r : x)));
	const setAccion = (i: number, k: number, a: AutoAccion) => setRegla(i, { ...reglas[i], acciones: reglas[i].acciones.map((x, j) => (j === k ? a : x)) });
	const agregar = (i: number, a: AutoAccion) => setRegla(i, { ...reglas[i], acciones: [...reglas[i].acciones, a] });
	const quitar = (i: number, k: number) => setRegla(i, { ...reglas[i], acciones: reglas[i].acciones.filter((_, j) => j !== k) });
	const conArchivo = (i: number, k: number, f: File) => {
		const clave = "a" + Date.now() + "_" + (sig++);
		setArchivos({ ...archivos, [clave]: f });
		setAccion(i, k, { tipo: "archivo", clave, nombre: f.name, mime: f.type, voz: false });
	};

	return (
		<div className="pl-auto">
			{reglas.map((r, i) => (
				<div key={i} className="pl-auto-regla">
					<div className="pl-auto-cab">
						<select value={r.disparo} disabled={!editable} onChange={(e) => setRegla(i, { ...r, disparo: e.target.value })}>
							{[r.disparo, ...libres].map((d) => <option key={d} value={d}>{nombreDisparo(d)}</option>)}
						</select>
						{editable ? <button className="pl-quitar" onClick={() => setReglas(reglas.filter((_, j) => j !== i))} aria-label="Quitar respuesta">×</button> : null}
					</div>
					{r.acciones.map((a, k) => (
						<div key={k} className="pl-auto-accion">
							{a.tipo === "texto" ? (
								<>
									<span className="pl-auto-tipo">Contestar</span>
									<div className="pl-auto-texto">
										<textarea rows={2} value={a.texto} disabled={!editable} placeholder="¡Gracias {{nombre}}! Ahorita le paso la información 😊"
											onChange={(e) => setAccion(i, k, { ...a, texto: e.target.value })} />
										{editable ? <div className="pl-auto-datos">{DATOS.map((d) => <button key={d.k} onClick={() => setAccion(i, k, { ...a, texto: a.texto + "{{" + d.k + "}}" })}>+ {d.t}</button>)}</div> : null}
									</div>
								</>
							) : a.tipo === "archivo" ? (
								<>
									<span className="pl-auto-tipo">Mandar archivo</span>
									<div className="pl-auto-archivo">
										<b>{a.nombre || "Sin archivo"}</b>
										{editable ? (
											<span className="pl-auto-botones">
												<label className="pl-btn chico">Subir foto, video o PDF
													<input type="file" hidden accept="image/jpeg,image/png,video/mp4,application/pdf"
														onChange={(e) => { const f = e.target.files?.[0]; if (f) conArchivo(i, k, f); }} />
												</label>
											</span>
										) : null}
									</div>
								</>
							) : a.tipo === "status" ? (
								<>
									<span className="pl-auto-tipo">Cambiar status a</span>
									<select value={a.status_id} disabled={!editable} onChange={(e) => setAccion(i, k, { tipo: "status", status_id: Number(e.target.value) })}>
										{statuses.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
									</select>
								</>
							) : (
								<span className="pl-auto-tipo">Avisarle a la asesora por Cliq</span>
							)}
							{editable ? <button className="pl-quitar" onClick={() => quitar(i, k)} aria-label="Quitar acción">×</button> : null}
						</div>
					))}
					{editable ? (
						<div className="pl-agregar">
							<button onClick={() => agregar(i, { tipo: "texto", texto: "" })}>+ Contestar texto</button>
							<button onClick={() => agregar(i, { tipo: "archivo", voz: false })}>+ Foto, video o PDF</button>
							{!r.acciones.some((a) => a.tipo === "status") ? <button onClick={() => agregar(i, { tipo: "status", status_id: statuses[0]?.id || 3 })}>+ Cambiar status</button> : null}
							{!r.acciones.some((a) => a.tipo === "avisar") ? <button onClick={() => agregar(i, { tipo: "avisar" })}>+ Avisar a la asesora</button> : null}
						</div>
					) : null}
				</div>
			))}
			{editable && libres.length > 0 ? (
				<button className="pl-btn chico" onClick={() => setReglas([...reglas, { disparo: libres[0], acciones: [{ tipo: "texto", texto: "" }] }])}>+ Respuesta automática</button>
			) : null}
			{!reglas.length && !editable ? <div className="pl-tenue">Sin respuestas automáticas.</div> : null}
		</div>
	);
};

export default AutoRespuestas;
